import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  ADDRESS,
  CLIENT_NAME,
  DESCRIPTION,
  ESTIMATED_END_DATE,
  ESTIMATED_START_DATE,
  NAME,
  PROJECT_CATEGORY_ID,
  PROJECT_CODE,
  PROJECT_ID,
  PROJECT_VALUE,
  STATUS,
} from "./consts";
import { useNavigate, useParams } from "react-router-dom";
import { schema, formValues } from "./validations";
import {
  getRequiredFields,
  projectStatus,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import {
  useAddProjectMutation,
  useGetProjectById,
  useGetProjectCategories,
  useUpdateProjectMutation,
} from "@/services";
import { routes } from "@/routes/utils";
import { setProjectData } from "@/redux/slices";
import { useEffect, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { toast } from "@/components";

const useProjectBasicDtl = () => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEdit = action == routes.edit;
  const isView = action == routes.view;

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [NAME]: "",
      [PROJECT_ID]: "",
      [PROJECT_CODE]: "",
      [PROJECT_CATEGORY_ID]: "",
      [ESTIMATED_START_DATE]: "",
      [ESTIMATED_END_DATE]: "",
      [CLIENT_NAME]: "",
      [PROJECT_VALUE]: "",
      [ADDRESS]: "",
      [DESCRIPTION]: "",
      [STATUS]: projectStatus.draft.value,
    },
  });

  const [keys, setKeys] = useState<any>({
    projectCategoryList: Math.random() * 2,
  });

  const requiredFields = getRequiredFields(schema);

  const { data: projectData, isLoading: isProjectDataLoading } =
    useGetProjectById({ id: id as string, queryParams: { enabled: !!id } });

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  const { data: projectCategoryList, isLoading: isLoadingProjectCategory } =
    useGetProjectCategories({ tenantId: loggedInUserData?.tenantId });

  useEffect(() => {
    if (projectData?.data) {
      dispatch(setProjectData(projectData?.data));
      form.reset({
        [NAME]: projectData?.data?.[NAME]?.toString() || "",
        [PROJECT_ID]: projectData?.data?.[PROJECT_ID]?.toString() || "",
        [PROJECT_CODE]: projectData?.data?.[PROJECT_CODE]?.toString() || "",
        [PROJECT_CATEGORY_ID]:
          projectData?.data?.[PROJECT_CATEGORY_ID]?.toString() || "",
        [ESTIMATED_START_DATE]: projectData?.data?.[ESTIMATED_START_DATE]
          ? moment(projectData?.data?.[ESTIMATED_START_DATE])
          : "",
        [ESTIMATED_END_DATE]: projectData?.data?.[ESTIMATED_END_DATE]
          ? moment(projectData?.data?.[ESTIMATED_END_DATE])
          : "",
        [CLIENT_NAME]: projectData?.data?.[CLIENT_NAME]?.toString() || "",
        [PROJECT_VALUE]: projectData?.data?.[PROJECT_VALUE]?.toString() || "",
        [ADDRESS]: projectData?.data?.[ADDRESS]?.toString() || "",
        [DESCRIPTION]: projectData?.data?.[DESCRIPTION]?.toString() || "",
        [STATUS]: projectData?.data?.[STATUS]?.toString() || "",
      });
      setKeys({ projectCategoryList: Math.random() * 3 });
    }
  }, [projectData]);

  const { mutate: addInfo, isPending: isAddPending } = useAddProjectMutation({
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
    onSuccess: (data: any) => {
      dispatch(setProjectData(data?.data?.data));
      toast({ message: "Basic Info Added Successfully" });
      navigate(
        `/${routes.admin}/${routes.projectOverview}/${data?.data?.data?.projectId}/${routes.add}/${routes.taskCategory}`
      );
    },
  });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateProjectMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: (data: any) => {
        dispatch(setProjectData(data?.data?.data));
        toast({ message: "Basic Info Updated Successfully" });
        navigate(
          `/${routes.admin}/${routes.projectOverview}/${id}/${action}/${routes.taskCategory}`
        );
      },
    });

  const onSubmit = (data: formValues) => {
    let _data = setEmptyFieldsToNull(data);
    _data = removeEmptyFields(_data);
    if (_data[PROJECT_ID]) {
      updateInfo({ id: _data[PROJECT_ID], data: { ..._data } });
    } else {
      delete (data as any)[PROJECT_ID];

      addInfo({
        data: {
          ..._data,
        },
      });
    }
  };

  return {
    form,
    onSubmit,
    isAddPending,
    requiredFields,
    isEdit,
    isProjectDataLoading,
    isUpdatePending,
    projectCategoryList,
    isLoadingProjectCategory,
    keys,
    isView,
  };
};

export default useProjectBasicDtl;
